/**
 * Versioned HTTP contract — agent-runner `v1` — for the agent-runner api
 * (`services/agent-runner-api`, mounted same-origin at `/api/agent-runner/v1/*`).
 *
 * Same rules as `./v1.js` / `./tracker-v1.js`: both sides parse the SAME Zod shapes
 * (ADR 0005); the `{data, error, meta}` envelope and error kinds are reused verbatim;
 * breaking changes ship as `agent-v2` alongside, never by mutating these.
 *
 * Owner scoping: the client supplies `ownerId` and the server scopes every row by it —
 * the same documented DEVOP-4 stopgap the idea-engine and tracker APIs carry today.
 */
import { z } from 'zod';
import { AgentArtifactSchema, AgentFindingSchema, AgentRunEventSchema, AgentRunSchema } from '../agent/domain.js';
// The lineage response reuses the Hub's own event shapes rather than restating them: the console is
// rendering Hub facts, and a second definition of an event summary is a second thing to drift.
import { EventSummarySchema, PossibleNextSchema } from './hub-v1.js';

export const AGENT_HTTP_CONTRACT_VERSION = 'agent-v1' as const;

const ownerId = z.string().min(1);

/** POST /api/agent-runner/v1/runs — start a new run. */
export const AgentRunStartRequestSchema = z.object({
  ownerId,
  prompt: z.string().min(1).max(4000),
  /** Opt-in per task (ADR 0069): capture the post-login session on success and auto-reuse it later. */
  persistSession: z.boolean().optional(),
});
export type AgentRunStartRequest = z.infer<typeof AgentRunStartRequestSchema>;

/** POST /api/agent-runner/v1/runs/:id/cancel */
export const AgentRunCancelRequestSchema = z.object({ ownerId });
export type AgentRunCancelRequest = z.infer<typeof AgentRunCancelRequestSchema>;

/** POST /api/agent-runner/v1/runs/:id/handoff — a human pauses the run to take over (ADR 0065 §3/§5). */
export const AgentRunHandoffRequestSchema = z.object({ ownerId });
export type AgentRunHandoffRequest = z.infer<typeof AgentRunHandoffRequestSchema>;

/** POST /api/agent-runner/v1/runs/:id/resume — a human hands control back; the agent continues. */
export const AgentRunResumeRequestSchema = z.object({ ownerId });
export type AgentRunResumeRequest = z.infer<typeof AgentRunResumeRequestSchema>;

/**
 * POST /api/agent-runner/v1/runs/:id/messages — send a follow-up into a live session (ADR 0078).
 * Accepted only while the session is `awaiting_input`; the container picks the message up and
 * continues the same browser (`add_new_task`).
 */
export const AgentRunMessageRequestSchema = z.object({ ownerId, prompt: z.string().min(1).max(4000) });
export type AgentRunMessageRequest = z.infer<typeof AgentRunMessageRequestSchema>;

/** POST /api/agent-runner/v1/runs/:id/close — end a live session (ADR 0078); the container exits. */
export const AgentRunCloseRequestSchema = z.object({ ownerId });
export type AgentRunCloseRequest = z.infer<typeof AgentRunCloseRequestSchema>;

/** GET /api/agent-runner/v1/runs/:id → one run (404 if absent/not owned). */
export const AgentRunResponseSchema = AgentRunSchema;

/** GET /api/agent-runner/v1/runs?ownerId=… → every run for the owner. */
export const AgentRunListResponseSchema = z.array(AgentRunSchema);

/** GET /api/agent-runner/v1/runs/:id/events?ownerId=… → the run's step log, in order. */
export const AgentRunEventsResponseSchema = z.array(AgentRunEventSchema);

/** GET /api/agent-runner/v1/runs/:id/artifacts?ownerId=… → the run's report/recording pointers. */
export const AgentArtifactsResponseSchema = z.array(AgentArtifactSchema);

/**
 * GET /api/agent-runner/v1/runs/:id/live-view?ownerId=… → the run's live Browser Run
 * view URL, gated by ownership (ADR 0065 §5 — a human watches/takes over here, never a
 * bare Browser Run URL). The gate is on THIS route; the URL itself is session-scoped.
 */
export const AgentLiveViewResponseSchema = z.object({ liveViewUrl: z.string().min(1) });
export type AgentLiveViewResponse = z.infer<typeof AgentLiveViewResponseSchema>;

/** GET /api/agent-runner/v1/runs/:id/findings?ownerId=… → the run's raw findings (§3.4). */
export const AgentFindingsResponseSchema = z.array(AgentFindingSchema);

/**
 * GET /api/agent-runner/v1/runs/:id/lineage?ownerId=… → what asked for this run, and what it
 * announced back (DEVOP-49 increment 6).
 *
 * The Hub already holds the answer: every fact about a run shares its `correlationId`, so one
 * `GET /api/hub/v1/trace/:correlationId` returns the whole chain plus whatever may still follow it.
 * This splits that chain into the event that *triggered* the run (the one whose id matches its
 * `sourceEventId`) and everything the run *emitted*.
 *
 * `state` is the load-bearing field, because **three different things look like "no events"** and
 * conflating them is how a UI tells a lie:
 *
 *   • `ready`            — the trail was read; `trigger`/`emitted` are the truth.
 *   • `not-event-driven` — this agent is not on the bus AT ALL. Nothing published an event to start
 *                          it and it publishes none back (a browser run: talent-hub calls the API
 *                          directly, or a person starts one by hand). Its step log is its record.
 *   • `unreachable`      — the Hub could not be read from here, so we do not know. Distinct from
 *                          "there are none", and never rendered as an empty list.
 */
export const AGENT_LINEAGE_STATES = ['ready', 'not-event-driven', 'unreachable'] as const;
export const AgentLineageStateSchema = z.enum(AGENT_LINEAGE_STATES);
export type AgentLineageState = z.infer<typeof AgentLineageStateSchema>;

export const AgentRunLineageSchema = z.object({
  runId: z.string().min(1),
  state: AgentLineageStateSchema,
  /** Why there is nothing to show, in words a person can act on. Null when `state` is `ready`. */
  reason: z.string().max(400).nullable(),
  /** The event that asked for this run. Null when nothing did, or when it is not in the trace. */
  trigger: EventSummarySchema.nullable(),
  /** Everything else on this run's correlation thread, oldest first — the facts it published. */
  emitted: z.array(EventSummarySchema),
  /**
   * What may still legitimately follow — empty once every lifecycle on this thread has ended
   * (ADR 0067, Amendment 2026-08-09). Never the branches a settled run has already foreclosed: a
   * completed run is not "waiting for" its own failure event.
   */
  possibleNext: z.array(PossibleNextSchema),
});
export type AgentRunLineage = z.infer<typeof AgentRunLineageSchema>;

/** Generic acknowledge payload for write endpoints (cancel). */
export const AgentAckSchema = z.object({ ok: z.literal(true) });

/**
 * The capabilities the Agent Runner's INTERNAL routes require (ADR 0112 increment 6).
 *
 * `/api/agent-runner/internal/v1/*` is the Container-facing surface — outside Access, proven by a
 * shared secret. The secret half is unchanged and still runs first: this is the policy layer sitting
 * ON TOP of it (ADR 0105 §7), so a request needs a valid token *and* a principal holding the
 * capability. What it buys is a named, revocable holder in the denial and the audit trail instead of
 * "whoever has the string".
 *
 * The split is by what a leak would cost, not by route count: reporting progress is cheap, calling
 * the LLM proxy spends money, and reading a sealed session touches stored credentials. The public
 * `/api/agent-runner/v1/*` surface is deliberately absent — a container token must never be able to
 * start, cancel or list runs, and the way to guarantee that is for no capability here to name them.
 */
export const AGENT_RUNNER_CAPABILITIES = {
  runReport: 'agent-runner:run.report',
  llmProxy: 'agent-runner:llm.proxy',
  sessionUse: 'agent-runner:session.use',
} as const;
